import { FilterConfig, CustomerFieldConfig } from "../types/AdHocTypes";
import { Api } from "../../../../../api/Api";

// Cache for field configurations
let cachedFieldConfigurations: CustomerFieldConfig[] = [];
let isLoading = false;
let loadPromise: Promise<CustomerFieldConfig[]> | null = null;

/**
 * Reactive getter for customer field configurations
 * This will return cached data immediately or load from API
 */
export const getCustomerFieldConfigs = async (): Promise<CustomerFieldConfig[]> => {
  return await loadFieldConfigurations();
};

/**
 * Loads field configurations from API with caching
 */
export const loadFieldConfigurations = async (): Promise<CustomerFieldConfig[]> => {
  // Return cached data if available
  if (cachedFieldConfigurations.length > 0) {
    return cachedFieldConfigurations;
  }

  // Return existing promise if already loading
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // Start loading
  isLoading = true;
  loadPromise = Api.getFieldConfigurations();

  try {
    const result = await loadPromise;
    cachedFieldConfigurations = result;
    return cachedFieldConfigurations;
  } finally {
    isLoading = false;
    loadPromise = null;
  }
};

/**
 * Gets cached field configurations (returns empty array if not loaded yet)
 */
export const getCachedFieldConfigurations = (): CustomerFieldConfig[] => {
  return cachedFieldConfigurations;
};

// Legacy export for backward compatibility (will be empty until API loads)
export const customerFieldConfigs: CustomerFieldConfig[] = [];

// The item fields available for filtering, derived from the provided receipt detail config.
// Note: labelKey is used for i18n translation lookup
export const itemInfoFields = [
  { id: "PRODUCT_GROUP_NUMBER", labelKey: "adHocSegments.itemFields.productGroupNumber" },
  { id: "PRODUCT_GROUP_DESC", labelKey: "adHocSegments.itemFields.productGroupDesc" },
  { id: "SUB_GROUP_DESC", labelKey: "adHocSegments.itemFields.subGroupDesc" },
  { id: "SALES_AREA_NUMBER", labelKey: "adHocSegments.itemFields.salesAreaNumber" },
  { id: "SALES_AREA_DESC", labelKey: "adHocSegments.itemFields.salesAreaDesc" },
  { id: "SUPPLIER_NUMBER", labelKey: "adHocSegments.itemFields.supplierNumber" },
  { id: "SUPPLIER_DESC", labelKey: "adHocSegments.itemFields.supplierDesc" },
  { id: "SUPPLIER_ITEM_TEXT", labelKey: "adHocSegments.itemFields.supplierItemText" },
  { id: "REF_NUMBER", labelKey: "adHocSegments.itemFields.refNumber" },
  { id: "SALES_PRICE", labelKey: "adHocSegments.itemFields.salesPrice" },
  { id: "ORIGINAL_PRICE", labelKey: "adHocSegments.itemFields.originalPrice" },
  { id: "PROMOTION", labelKey: "adHocSegments.itemFields.promotion" },
  { id: "SEASON", labelKey: "adHocSegments.itemFields.season" },
  { id: "EAN", labelKey: "adHocSegments.itemFields.ean" },
  { id: "QUANTITY", labelKey: "adHocSegments.itemFields.quantity" },
  { id: "NEWARTICLE", labelKey: "adHocSegments.itemFields.newArticle" },
  { id: "testnewkey", labelKey: "adHocSegments.itemFields.receiptDetailConfig" },
  { id: "NEW_FILTER_THIEN_ITEM_INFO", labelKey: "adHocSegments.itemFields.testBug" },
  { id: "BONUS_RULE_APPLIED", labelKey: "adHocSegments.itemFields.bonusRuleApplied" },
  { id: "SUB_GROUP_NUMBER", labelKey: "adHocSegments.itemFields.subGroupNumber" },
];

/**
 * Gets cached filter configs (returns empty array if not loaded yet)
 */
export const getCachedFilterConfigs = (): FilterConfig[] => {
  const cachedFields = getCachedFieldConfigurations();

  if (cachedFields.length === 0) {
    return [];
  }

  return [
    ...cachedFields.map((field: CustomerFieldConfig) => ({
      id: field.fieldName,
      label: field.fieldDisplayName,
      inputType: getInputTypeFromDataType(field.dataType),
      operators: field.availableOperators.map((op: any) => op.description),
      fieldId: field.id,
      fieldName: field.fieldName,
      dataType: field.dataType,
      availableOperators: field.availableOperators,
      allowedValues: field.allowedValues,
    })),
  ];
};

/**
 * Reactive getter for filter configurations
 * This will return cached data immediately or load from API
 */
export const getFilterConfigs = async (): Promise<FilterConfig[]> => {
  const fieldConfigs = await loadFieldConfigurations();

  return [
    ...fieldConfigs.map((field) => ({
      id: field.fieldName,
      label: field.fieldDisplayName,
      inputType: getInputTypeFromDataType(field.dataType),
      operators: field.availableOperators.map((op) => op.description),
      fieldId: field.id,
      fieldName: field.fieldName,
      dataType: field.dataType,
      availableOperators: field.availableOperators,
      allowedValues: field.allowedValues,
    })),
  ];
};

// Legacy export for backward compatibility (will be empty until API loads)
export const filterConfigs: FilterConfig[] = [];

// Helper function to determine input type based on data type
function getInputTypeFromDataType(dataType: string): "text" | "date" | "boolean" | "number" {
  switch (dataType) {
    case "STRING":
      return "text";
    case "DATE":
    case "TIMESTAMP":
      return "date";
    case "BOOLEAN":
      return "boolean";
    case "NUMBER":
    case "DECIMAL":
      return "number";
    default:
      return "text";
  }
}
